export const veridionPageEN = {
  "seo": {
    "title": "Veridion Agentic Company Intelligence | Organisation as a Service",
    "description": "The next stage of productivity: Organisation as a Service. AI staff integrate into every process, learn on their own and create missing capabilities themselves, on premises or in your own cloud."
  },
  "hero": {
    "kicker": "Veridion Agentic Company Intelligence",
    "title": "One creator, infinitely many solutions.",
    "claim": "The next stage of productivity: Organisation as a Service.",
    "lead": "The company AI that steers all of pAIoneer Labs GmbH: a workforce of AI staff that integrates into every process of your company, carries work to the end on its own and learns with every task. People set the direction. Everything else happens by itself."
  },
  "vision": {
    "lines": [
      "Software gave companies tools.",
      "AI gave them assistants.",
      "Veridion gives them an organisation."
    ],
    "note": "Information moves in hours, experience retires, positions stay unfilled. One more tool does not solve that; a workforce you do not have to hire does."
  },
  "grow": {
    "title": "What it cannot do, it creates",
    "text": "Whenever a capability does not yet exist for a task or something cannot be solved with existing means, Veridion develops new, arbitrarily complex AI systems on its own — AI develops AI further: designed, built, verified, and put into operation. Only the release stays with the human via [Intent-Driven Development](idd) (IDD).",
    "punch": "That is exactly what it means: one creator, infinitely many solutions."
  },
  "integrate": {
    "title": "In the middle of your workflows",
    "subtitle": "No new software, no training, no migration.",
    "cards": [
      {
        "title": "In the company chat",
        "text": "The conversation happens where your people already write: Webex, Microsoft Teams, Zoom, Slack. One message is enough."
      },
      {
        "title": "In your tools",
        "text": "Project management, repositories, filing: the work happens in the systems that are there, not around them."
      },
      {
        "title": "From the offer to the bookkeeping",
        "text": "One process after another, at your pace. When capacity runs short, also just a single development package or a whole project."
      }
    ]
  },
  "org": {
    "title": "A workforce, organised like a company",
    "subtitle": "We never force a rigid template onto a company: for any company structure we design a custom organigram, assign clear roles and responsibilities, and adapt flexibly. Every Business Unit consists of AI employees orchestrated by Veridion that work fully automatically right up to the [IDD](idd) Human-in-the-Loop — and whenever something is not yet possible, Veridion develops new AI itself.",
    "execKicker": "Coordination Layer — Fully Automated Up to the IDD Human-in-the-Loop",
    "execTitle": "Custom organigram, clear roles, and AI that develops AI further.",
    "execText": "Below the human decision-makers, the coordination layer and the Business Units of AI employees operate fully automatically: they decompose goals, assign work across clearly defined roles and responsibilities, solve problems autonomously, and obtain targeted approvals from humans. The human in the loop steers via [Intent-Driven Development](idd) (IDD) — far beyond software code as the shared steering discipline of human and AI across intents, decisions, and verification scenarios. And whenever a capability is missing, Veridion autonomously designs and builds new, arbitrarily complex AI systems — AI develops AI further.",
    "units": [
      {
        "name": "Software development",
        "text": "Owns software from the requirement to operation: architecture, code, tests, reviews, pipelines and the quality behind them."
      },
      {
        "name": "Electronics & firmware",
        "text": "Owns the hardware side: circuits, firmware and the choice of every component."
      },
      {
        "name": "Quantum computing & quantum AI",
        "text": "Owns the research on quantum computing and quantum AI and turns solid results into products."
      },
      {
        "name": "Sales & marketing",
        "text": "Owns the market: watches it, finds target customers, writes dossiers, drafts texts and offers. A person closes the deal."
      },
      {
        "name": "Products & platform operation",
        "text": "Owns products and operation: strategy, priorities, releases, plus monitoring, incidents and capacity of the running platform."
      },
      {
        "name": "Administration & finance",
        "text": "Owns the commercial side: receipts, invoices, contracts, deadlines, prepared up to the signature, which stays with you."
      }
    ]
  },
  "trust": {
    "title": "What you can rely on",
    "cards": [
      {
        "title": "Human-in-the-loop & IDD far beyond code",
        "text": "Intent-Driven Development ([IDD](idd)) reaches far beyond software code: it is what the human in the loop practices together with the AI across every area of the company. Humans and AI jointly formulate the shared intent, settle binding decisions and define verifiable scenarios before the AI workforce executes autonomously in fixed, plannable cycles and obtains human approval."
      },
      {
        "title": "Communication optimised by game theory",
        "text": "Project management and the communication of the AI staff are optimised by game theory, from the single AI employee to the whole business unit: thoroughness and justified objection pay off; waving things through, repetition and passing responsibility cost. This is how the best communication wins."
      },
      {
        "title": "Explainable down to the source",
        "text": "Explainable AI is the working principle here, not an add-on: every result lays open what it came from, the passages in the company knowledge, the data, the calculation path. What has no evidence is not asserted."
      },
      {
        "title": "Learns on its own",
        "text": "Its own numbers are read after every pass and its own rules adjusted accordingly: every running project makes the whole better."
      }
    ]
  },
  "daily": {
    "title": "What it already takes over today",
    "subtitle": "Every day, across the company.",
    "items": [
      {
        "title": "Development",
        "text": "Code, tests and documentation from recorded decisions. Modernisation of grown software with golden master and back-to-back proof."
      },
      {
        "title": "Electronics & firmware",
        "text": "Analysing datasheets, simulating circuits as digital twins, developing firmware and judging component alternatives before anything is built, close to the hardware down to the sensor."
      },
      {
        "title": "Quantum research",
        "text": "Reading the state of the art, building and evaluating reproducible experiments against classical baselines, every result recorded traceably."
      },
      {
        "title": "Data analysis",
        "text": "SQL Server, MariaDB, MySQL and Oracle: a typed analysis plan, strictly read-only queries, statistics computed by the runtime, reports from one versioned result."
      },
      {
        "title": "Administration & finance",
        "text": "Bookkeeping, accounting and the bureaucracy a company brings: reading, assigning and filing receipts; drafts for invoices and contracts included."
      },
      {
        "title": "Projects & tasks",
        "text": "A meeting transcript turns into project-management tasks without detours, and progress flows back: the next meeting starts from the current state."
      },
      {
        "title": "Company knowledge",
        "text": "A knowledge base for documents and a graph database linking tasks, decisions, people and systems: context across weeks and across project boundaries."
      },
      {
        "title": "Research on the internet",
        "text": "What the company knowledge does not hold gets researched: searched, the best pages read, and at the end stands a report in which every statement has its numbered source."
      },
      {
        "title": "Voice & meetings",
        "text": "The whole company can be led on the move: spoken conversations do the same work as the chat, on the phone while travelling as well."
      },
      {
        "title": "Office work with RoBuddy",
        "text": "RoBuddy covers the workplace: appointments, e-mails, training and data protection questions. The units cover the rest of the company."
      }
    ]
  },
  "operate": {
    "title": "Operation",
    "cards": [
      {
        "title": "On premises or your own cloud",
        "text": "Up front we calculate together which processes pay off: return on investment and total cost lie open. Operation runs on your hardware or in your own cloud, and no document leaves the house."
      },
      {
        "title": "At ours",
        "text": "pAIoneer Labs GmbH is built this way from the ground up: our projects, our company knowledge and our administration run on it. The organisation develops the [trading assistant](finance) on our platform itself and is the AI in its chat."
      }
    ]
  },
  "cta": {
    "title": "Enquiry",
    "subtitle": "As server software in your infrastructure. The price is given on request.",
    "button": "Send enquiry",
    "subject": "Veridion Agentic Company Intelligence"
  }
};
